import React, { useMemo, useState } from 'react';
import { ArrowLeft, Info, ChevronLeft, ChevronRight, Snowflake } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import {
  Container, NavOverlay, NavButton, TooltipBox,
  EdgeArrow, PivotCounter, ZoomControlsPanel, ZoomButton, ZoomLevel,
  SvgDefs, useZoomToFit, getAdaptiveTextParams,
  DEFAULT_LINE_WIDTH, wrapText, getScenarioColor,
  buildGradientStops, estimateLabelHeight,
} from './branchViewShared';

/**
 * Cardinal (4-directional) tree layout for pivot points.
 *
 * Each scenario occupies one side (top / right / bottom / left).
 * Tree structure grows from the edge inward toward the central pivot point:
 *   Scenario label → Variable nodes → Member leaves → Pivot center
 *
 * Connectors are smooth Bézier curves, like the Impact Path tree.
 */

const SIDE_ASSIGNMENTS = {
  1: ['top'],
  2: ['top', 'bottom'],
  3: ['top', 'right', 'bottom'],
  4: ['top', 'right', 'bottom', 'left'],
};

/** Smooth S-curve Bézier path between two points, flowing along the side's axis. */
function makeConnector(x1, y1, x2, y2, side) {
  const isVertical = side === 'top' || side === 'bottom';
  if (isVertical) {
    const midY = (y1 + y2) / 2;
    return `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`;
  }
  const midX = (x1 + x2) / 2;
  return `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`;
}

export default function PivotPointBranchViewCardinal({
  pivotPoint,
  memberNodes,
  scenarios,
  width,
  height,
  onBack,
  onInfoClick,
  onMemberClick,
  currentIndex,
  totalCount,
  onPrev,
  onNext,
  layoutMode,
  onToggleLayout,
}) {
  const { t } = useTranslation();
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  // --- Lookups ---
  const scenarioById = useMemo(() => {
    const map = {};
    (scenarios || []).forEach(s => { map[s.id] = s; });
    return map;
  }, [scenarios]);

  const variableById = useMemo(() => {
    const map = {};
    (scenarios || []).forEach(s => {
      (s.variables || []).forEach(v => {
        map[v.id] = { ...v, scenarioId: s.id, scenarioTitle: s.title, color: s.color };
      });
    });
    return map;
  }, [scenarios]);

  // --- Group: scenario → variable → members ---
  const treeData = useMemo(() => {
    const scenarioMap = new Map();
    (memberNodes || []).forEach(member => {
      const sId = member.scenarioId || 'unknown';
      if (!scenarioMap.has(sId)) {
        scenarioMap.set(sId, { scenarioId: sId, variables: new Map() });
      }
      const varId = member.connectedVariables?.[0] || 'unknown';
      const vars = scenarioMap.get(sId).variables;
      if (!vars.has(varId)) vars.set(varId, []);
      vars.get(varId).push(member);
    });

    return Array.from(scenarioMap.entries()).map(([sId, data]) => {
      const scenario = scenarioById[sId];
      return {
        scenarioId: sId,
        title: scenario?.title || sId,
        color: scenario?.color || getScenarioColor(scenario?.title) || '#607d8b',
        variables: Array.from(data.variables.entries()).map(([vId, members]) => ({
          variableId: vId,
          title: variableById[vId]?.title || 'Tuntematon',
          members,
        })),
      };
    });
  }, [memberNodes, scenarioById, variableById]);

  // --- Cardinal layout ---
  const layout = useMemo(() => {
    const cx = width / 2;
    const cy = height / 2;
    const minDim = Math.min(width, height);
    const padding = 40;

    const centerRadius = Math.max(28, minDim * 0.035);
    const scenarioNodeR = Math.max(16, minDim * 0.02);
    const variableNodeR = Math.max(11, minDim * 0.014);
    const leafNodeR = Math.max(7, minDim * 0.01);

    // Distance layers from center (as fraction of available space)
    // Without scenario/variable nodes, leaves spread across the full radius
    const memberLayer = 0.70;
    const variableLayer = 0.58; // kept for layout math, not rendered
    const scenarioLayer = 0.88; // kept for layout math, not rendered

    const count = Math.min(treeData.length, 4);
    const sides = SIDE_ASSIGNMENTS[count] || SIDE_ASSIGNMENTS[4];

    const branches = treeData.slice(0, 4).map((scenarioGroup, idx) => {
      const side = sides[idx];
      const isVert = side === 'top' || side === 'bottom';
      const sign = (side === 'top' || side === 'left') ? -1 : 1;
      const avail = isVert ? (cy - padding) : (cx - padding);

      // Scenario label position
      const sx = isVert ? cx : cx + sign * avail * scenarioLayer;
      const sy = isVert ? cy + sign * avail * scenarioLayer : cy;

      // Perpendicular spread space
      const spreadTotal = isVert ? (width - padding * 3) : (height - padding * 3);
      const varCount = scenarioGroup.variables.length;
      const totalMembers = scenarioGroup.variables.reduce((s, v) => s + v.members.length, 0);

      // Allocate spread width per variable proportional to its member count
      const children = scenarioGroup.variables.map((varGroup, vIdx) => {
        const memberCount = varGroup.members.length;

        // Variable perpendicular position — proportional allocation
        const varWeight = totalMembers > 0 ? memberCount / totalMembers : 1 / varCount;
        // Cumulative offset for this variable
        let cumulativeWeight = 0;
        for (let i = 0; i < vIdx; i++) {
          cumulativeWeight += scenarioGroup.variables[i].members.length / Math.max(totalMembers, 1);
        }
        const varCenter = -0.5 + cumulativeWeight + varWeight / 2; // [-0.5, 0.5] range
        const varSpread = varCenter * spreadTotal;

        const vx = isVert ? cx + varSpread : cx + sign * avail * variableLayer;
        const vy = isVert ? cy + sign * avail * variableLayer : cy + varSpread;

        // Members within this variable's allocated band
        // Saw-tooth stagger: alternate leaves at different distances from center
        // so labels don't overlap on the straight axis
        const memberBandWidth = varWeight * spreadTotal * 0.95;
        const staggerAmount = avail * 0.12; // depth offset for alternating leaves
        const leaves = varGroup.members.map((member, mIdx) => {
          let memberOffset;
          if (memberCount === 1) {
            memberOffset = varSpread;
          } else {
            const start = varSpread - memberBandWidth / 2;
            memberOffset = start + (mIdx / (memberCount - 1)) * memberBandWidth;
          }

          // Saw-tooth: odd indices push further from center, even stay closer
          const depthLayer = memberLayer + (mIdx % 2 === 0 ? 0 : staggerAmount / avail);

          return {
            ...member,
            x: isVert ? cx + memberOffset : cx + sign * avail * depthLayer,
            y: isVert ? cy + sign * avail * depthLayer : cy + memberOffset,
            nodeRadius: leafNodeR,
          };
        });

        return {
          variableId: varGroup.variableId,
          title: varGroup.title,
          x: vx, y: vy,
          nodeRadius: variableNodeR,
          leaves,
          memberCount,
          color: scenarioGroup.color,
          side,
        };
      });

      return {
        ...scenarioGroup,
        side,
        x: sx, y: sy,
        nodeRadius: scenarioNodeR,
        children,
        totalMembers,
      };
    });

    return { cx, cy, centerRadius, branches };
  }, [width, height, treeData]);

  // --- Center gradient ---
  const centerGradientStops = useMemo(() => {
    const names = pivotPoint?.scenarioNames || [];
    return names.length >= 2 ? buildGradientStops(names) : null;
  }, [pivotPoint]);

  const centerFill = useMemo(() => {
    if (centerGradientStops) return 'url(#center-gradient)';
    const names = pivotPoint?.scenarioNames || [];
    if (names.length === 1) return getScenarioColor(names[0]);
    return '#9C27B0';
  }, [pivotPoint, centerGradientStops]);

  // --- Zoom ---
  const { svgRef, zoomLevel, handleZoomIn, handleZoomOut, handleZoomReset } =
    useZoomToFit({ pivotId: pivotPoint?.id, layout, width, height });

  // --- Hover ---
  const isHighlighted = (nodeId) => !hoveredNodeId || nodeId === hoveredNodeId;
  const dimOpacity = hoveredNodeId ? 0.15 : 1;

  const handleMouseEnter = (node, event) => {
    setHoveredNodeId(node.id);
    const scenarioName = scenarioById[node.scenarioId]?.title || '';
    setTooltip({
      x: event.clientX + 12, y: event.clientY - 10,
      title: node.text || node.title || '',
      scenarioName, scenarioColor: getScenarioColor(scenarioName),
      participant: node.participant,
    });
  };
  const handleMouseLeave = () => { setHoveredNodeId(null); setTooltip(null); };

  // --- Label anchor helper ---
  const labelAnchor = (side) => {
    if (side === 'left') return 'end';
    if (side === 'right') return 'start';
    return 'middle';
  };

  const labelOffset = (side, radius) => {
    if (side === 'left') return { dx: -(radius + 10), dy: 4 };
    if (side === 'right') return { dx: radius + 10, dy: 4 };
    return { dx: 0, dy: radius + 14 };
  };

  return (
    <Container>
      <NavOverlay>
        {onBack && (
          <NavButton onClick={onBack}><ArrowLeft /> {t('pivotPoints.common.back')}</NavButton>
        )}
        <NavButton onClick={onInfoClick}><Info /> {t('pivotPoints.common.info')}</NavButton>
        {onToggleLayout && (
          <NavButton onClick={onToggleLayout} title={t('pivotPoints.branch.snowflakeTitle')}>
            <Snowflake /> {t('pivotPoints.branch.snowflake')}
          </NavButton>
        )}
      </NavOverlay>

      {totalCount > 1 && (
        <>
          <EdgeArrow $side="left" onClick={onPrev} disabled={currentIndex <= 0} title={t('pivotPoints.common.previousPivot')}>
            <ChevronLeft />
          </EdgeArrow>
          <EdgeArrow $side="right" onClick={onNext} disabled={currentIndex >= totalCount - 1} title={t('pivotPoints.common.nextPivot')}>
            <ChevronRight />
          </EdgeArrow>
          <PivotCounter>{currentIndex + 1} / {totalCount}</PivotCounter>
        </>
      )}

      <svg ref={svgRef} width={width} height={height}>
        <SvgDefs centerGradientStops={centerGradientStops} />
        <g className="zoomable-content">

          {/* === Zoom-adaptive rendering === */}
          {(() => {
            const totalLeaves = layout.branches.reduce((s, b) => s + b.children.reduce((s2, v) => s2 + v.leaves.length, 0), 0);
            const tp = getAdaptiveTextParams(totalLeaves, zoomLevel);

            return (
              <>
              {/* === Connectors: center → members === */}
              {layout.branches.map((branch, bi) =>
                branch.children.map((varNode, vi) =>
                  varNode.leaves.map((leaf, li) => (
                    <path key={`mp-${bi}-${vi}-${li}`}
                      d={makeConnector(leaf.x, leaf.y, layout.cx, layout.cy, branch.side)}
                      fill="none" stroke={branch.color} strokeWidth={1}
                      strokeOpacity={isHighlighted(leaf.id) ? 0.25 : dimOpacity * 0.08}
                      style={{ transition: 'stroke-opacity 0.2s ease' }} />
                  ))
                )
              )}

              {/* === Member leaf nodes === */}
              {layout.branches.map((branch, bi) =>
                branch.children.map((varNode, vi) =>
                  varNode.leaves.map((leaf, li) => {
                    const highlighted = isHighlighted(leaf.id);
                    const { lines, truncated } = wrapText(leaf.text || '', tp.labelWidth, tp.maxLabelLines);
                    const lOff = labelOffset(branch.side, leaf.nodeRadius);
                    return (
                      <g key={`leaf-${bi}-${vi}-${li}`}
                        style={{ cursor: 'pointer', transition: 'opacity 0.2s ease' }}
                        opacity={highlighted ? 1 : dimOpacity}
                        onMouseEnter={(e) => handleMouseEnter(leaf, e.nativeEvent)}
                        onMouseLeave={handleMouseLeave}
                        onClick={() => onMemberClick?.(leaf)}>
                        <circle cx={leaf.x} cy={leaf.y} r={leaf.nodeRadius}
                          fill={branch.color} fillOpacity={0.75}
                          stroke={highlighted && hoveredNodeId ? 'white' : 'rgba(255,255,255,0.3)'}
                          strokeWidth={highlighted && hoveredNodeId ? 1.5 : 0.5}
                          filter={highlighted && hoveredNodeId ? 'url(#leaf-glow)' : undefined} />
                        {tp.showLeafText && (
                          <text x={leaf.x + lOff.dx} y={leaf.y + lOff.dy}
                            textAnchor={labelAnchor(branch.side)}
                            fill="rgba(255,255,255,0.85)" fontSize={`${tp.leafFontSize}px`} fontWeight="500">
                            {lines.map((line, idx) => (
                              <tspan key={idx} x={leaf.x + lOff.dx} dy={idx === 0 ? 0 : tp.lineHeight}>{line}</tspan>
                            ))}
                          </text>
                        )}
                      </g>
                    );
                  })
                )
              )}

              {/* === Center pivot node === */}
              <g style={{ cursor: 'pointer' }} onClick={onInfoClick} filter="url(#center-glow)">
                <circle cx={layout.cx} cy={layout.cy} r={layout.centerRadius}
                  fill={centerFill} fillOpacity={0.9}
                  stroke="rgba(255,255,255,0.6)" strokeWidth={2} />
                <text x={layout.cx} y={layout.cy} textAnchor="middle" dominantBaseline="central"
                  fill="white" fontSize={`${tp.centerFontSize}px`} fontWeight="700">
                  {memberNodes?.length || 0}
                </text>
                {(() => {
                  const { lines: titleLines } = wrapText(pivotPoint?.text || pivotPoint?.title || '', 24, 3);
                  const startY = layout.cy + layout.centerRadius + 18;
                  return (
                    <text x={layout.cx} y={startY} textAnchor="middle" fill="white" fontSize="14px" fontWeight="700">
                      {titleLines.map((line, idx) => (
                        <tspan key={idx} x={layout.cx} dy={idx === 0 ? 0 : 16}>{line}</tspan>
                      ))}
                    </text>
                  );
                })()}
              </g>
              </>
            );
          })()}

        </g>
      </svg>

      <ZoomControlsPanel>
        <ZoomButton onClick={handleZoomIn} title={t('pivotPoints.common.zoomIn')}>+</ZoomButton>
        <ZoomLevel>{Math.round(zoomLevel * 100)}%</ZoomLevel>
        <ZoomButton onClick={handleZoomOut} title={t('pivotPoints.common.zoomOut')}>−</ZoomButton>
        <ZoomButton onClick={handleZoomReset} title={t('pivotPoints.common.zoomReset')} style={{ fontSize: '0.9rem' }}>⟲</ZoomButton>
      </ZoomControlsPanel>

      {tooltip && (
        <TooltipBox style={{ left: tooltip.x, top: tooltip.y }}>
          <div className="tooltip-title">{tooltip.title}</div>
          {tooltip.scenarioName && (
            <span className="tooltip-scenario" style={{
              background: `${tooltip.scenarioColor}33`, color: tooltip.scenarioColor,
              border: `1px solid ${tooltip.scenarioColor}66`,
            }}>{tooltip.scenarioName}</span>
          )}
          {tooltip.participant && <div className="tooltip-meta">{tooltip.participant}</div>}
        </TooltipBox>
      )}
    </Container>
  );
}
